import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const feedbackImages = [
  {
    id: 1,
    src: 'https://customer-assets.emergentagent.com/job_cf02698c-9bfd-4a23-bd23-6ebf9f35356f/artifacts/38o95lwo_print7.jpeg',
    alt: 'Depoimento paciente - Transformação de vida'
  },
  {
    id: 2,
    src: 'https://customer-assets.emergentagent.com/job_cf02698c-9bfd-4a23-bd23-6ebf9f35356f/artifacts/cu64vps7_print3.jpeg',
    alt: 'Depoimento paciente - Perda de 10kg'
  },
  {
    id: 3,
    src: 'https://customer-assets.emergentagent.com/job_cf02698c-9bfd-4a23-bd23-6ebf9f35356f/artifacts/muym1mp7_print6.jpeg',
    alt: 'Depoimento paciente - Roupas mais largas'
  },
  {
    id: 4,
    src: 'https://customer-assets.emergentagent.com/job_cf02698c-9bfd-4a23-bd23-6ebf9f35356f/artifacts/vpwhfp8k_print4.jpeg',
    alt: 'Depoimento paciente - Progresso nos treinos'
  },
  {
    id: 5,
    src: 'https://customer-assets.emergentagent.com/job_cf02698c-9bfd-4a23-bd23-6ebf9f35356f/artifacts/zfdmy4me_print5.jpeg',
    alt: 'Depoimento paciente - Resultados com exercício'
  }
];

const PhoneFrame = ({ image, variant }) => {
  const isCenter = variant === 'center';

  return (
    <div className="relative" style={{ width: isCenter ? '280px' : '230px' }}>
      {/* Phone body */}
      <div
        className="relative rounded-[2.5rem] overflow-hidden"
        style={{
          border: '3px solid #333',
          background: 'linear-gradient(145deg, #2a2a2a 0%, #1a1a1a 100%)',
          boxShadow: isCenter
            ? '0 0 80px rgba(17, 131, 15, 0.15), 0 30px 80px rgba(0,0,0,0.6)'
            : '0 20px 50px rgba(0,0,0,0.5)',
        }}
      >
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[35%] h-[22px] bg-black rounded-b-2xl z-20" />

        {/* Screen area */}
        <div className="bg-black rounded-[2.2rem] overflow-hidden m-[3px]">
          {/* Status bar spacer */}
          <div className="h-[24px] bg-black" />

          {/* Screenshot */}
          <div style={{ aspectRatio: '9/17' }}>
            <img
              src={image.src}
              alt={image.alt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>

          {/* Bottom spacer */}
          <div className="h-[4px] bg-black" />
        </div>

        {/* Home indicator */}
        <div className="flex justify-center py-1.5">
          <div className="w-[30%] h-[4px] rounded-full bg-gray-600" />
        </div>
      </div>

      {/* Glass reflection */}
      <div
        className="absolute inset-0 rounded-[2.5rem] pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, transparent 35%)',
        }}
      />

      {/* Green glow under center phone */}
      {isCenter && (
        <div
          className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-[90%] h-20"
          style={{
            background: 'radial-gradient(ellipse, rgba(17, 131, 15, 0.3) 0%, transparent 70%)',
            filter: 'blur(16px)',
          }}
        />
      )}
    </div>
  );
};

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const total = feedbackImages.length;

  useEffect(() => {
    const check = () => {
      if (window.innerWidth < 640) setVisibleCount(1);
      else if (window.innerWidth < 1024) setVisibleCount(2);
      else setVisibleCount(3);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchMove = (e) => { touchEndX.current = e.touches[0].clientX; };
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) { diff > 0 ? goNext() : goPrev(); }
  };

  const getIdx = (offset) => (currentIndex + offset + total) % total;
  const leftIdx = getIdx(-1);
  const centerIdx = currentIndex;
  const rightIdx = getIdx(1);

  return (
    <section className="relative overflow-hidden bg-black" id="depoimentos" data-testid="testimonials-section">
      {/* ===== HEADER ===== */}
      <div className="relative pt-20 md:pt-28 pb-12 md:pb-16 overflow-hidden">
        {/* Green glow */}
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px]"
          style={{
            background: 'radial-gradient(ellipse, rgba(17, 131, 15, 0.12) 0%, transparent 60%)',
            filter: 'blur(50px)',
          }}
        />

        <div className="relative z-10 text-center px-6">
          {/* Stacked Feedback text */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative mb-8 md:mb-10"
          >
            <div className="relative inline-block">
              {/* Outline (behind) */}
              <div
                className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black tracking-tight select-none leading-none"
                style={{
                  fontFamily: 'Playfair Display, serif',
                  WebkitTextStroke: '1.5px rgba(17, 131, 15, 0.4)',
                  WebkitTextFillColor: 'transparent',
                }}
                data-testid="feedback-outline-text"
              >
                Feedback
              </div>

              {/* Filled (front, overlapping) */}
              <div
                className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black tracking-tight select-none leading-none"
                style={{
                  fontFamily: 'Playfair Display, serif',
                  background: 'linear-gradient(135deg, #11830f 0%, #1aad17 40%, #0f7a0d 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginTop: '-0.7em',
                }}
                data-testid="feedback-filled-text"
              >
                Feedback
              </div>
            </div>
          </motion.div>

          {/* Pill badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div
              className="inline-block bg-white rounded-full px-8 py-3 shadow-[0_4px_30px_rgba(255,255,255,0.1)]"
              data-testid="feedback-pill-badge"
            >
              <span className="text-[#1A1A1A] font-bold text-sm sm:text-base md:text-lg">
                O que os pacientes estão falando?
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ===== CAROUSEL ===== */}
      <div className="relative pb-20 md:pb-28">
        {/* Bottom green glow */}
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-[250px]"
          style={{
            background: 'radial-gradient(ellipse at bottom, rgba(17, 131, 15, 0.05) 0%, transparent 60%)',
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4">
          {/* Phones container */}
          <motion.div
            initial={{ opacity: 0, y: 50, filter: 'blur(10px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="flex items-center justify-center py-6 md:py-10"
            style={{ minHeight: '500px' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            data-testid="feedback-carousel"
          >
            {/* Left phone */}
            {visibleCount >= 3 && (
              <motion.div
                key={`left-${leftIdx}`}
                initial={{ opacity: 0, x: -60, scale: 0.7 }}
                animate={{ opacity: 0.5, x: 0, scale: 1, rotateY: -8 }}
                transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
                className="flex-shrink-0 hidden lg:block"
                style={{ perspective: '1200px', filter: 'blur(1.5px)', marginRight: '-30px', zIndex: 1 }}
              >
                <PhoneFrame image={feedbackImages[leftIdx]} variant="side" />
              </motion.div>
            )}

            {/* Center phone */}
            <motion.div
              key={`center-${centerIdx}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
              className="flex-shrink-0 relative z-10"
              whileHover={{ scale: 1.03 }}
            >
              <PhoneFrame image={feedbackImages[centerIdx]} variant="center" />
            </motion.div>

            {/* Right phone */}
            {visibleCount >= 3 && (
              <motion.div
                key={`right-${rightIdx}`}
                initial={{ opacity: 0, x: 60, scale: 0.7 }}
                animate={{ opacity: 0.5, x: 0, scale: 1, rotateY: 8 }}
                transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
                className="flex-shrink-0 hidden lg:block"
                style={{ perspective: '1200px', filter: 'blur(1.5px)', marginLeft: '-30px', zIndex: 1 }}
              >
                <PhoneFrame image={feedbackImages[rightIdx]} variant="side" />
              </motion.div>
            )}

            {/* Tablet: show only right phone extra */}
            {visibleCount === 2 && (
              <motion.div
                key={`right-tab-${rightIdx}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5, scale: 0.9, rotateY: 6 }}
                transition={{ duration: 0.45 }}
                className="flex-shrink-0 hidden sm:block lg:hidden"
                style={{ perspective: '1200px', filter: 'blur(1px)', marginLeft: '-20px', zIndex: 1 }}
              >
                <PhoneFrame image={feedbackImages[rightIdx]} variant="side" />
              </motion.div>
            )}
          </motion.div>

          {/* Navigation controls */}
          <div className="flex items-center justify-center gap-6 mt-6">
            <button
              onClick={goPrev}
              className="w-12 h-12 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-brand-green/20 hover:border-brand-green/40 transition-all duration-300 text-white"
              data-testid="feedback-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2.5">
              {feedbackImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`rounded-full transition-all duration-300 ${
                    idx === currentIndex
                      ? 'bg-brand-green w-8 h-3'
                      : 'bg-white/15 w-3 h-3 hover:bg-white/30'
                  }`}
                  data-testid={`feedback-dot-${idx}`}
                />
              ))}
            </div>

            <button
              onClick={goNext}
              className="w-12 h-12 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-brand-green/20 hover:border-brand-green/40 transition-all duration-300 text-white"
              data-testid="feedback-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
